import type { ReactNode } from 'react'
import { format } from 'date-fns'
import { ChevronRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { notificationCreatedAtDate } from '../utils/userDrawerHelpers'
import type {
  Notification,
  NotificationDetail,
} from '@/lib/api/notificationService'
import {
  buildNotificationDetailSection,
} from '../utils/notificationDetailHelpers'
import type { NotificationLinkTarget } from '../types/utils'

interface NotificationDetailPanelProps {
  mailContent: Notification
  detail: NotificationDetail
  onBack: () => void
  onCloseDrawer: () => void
}

function NotificationLink({
  target,
  children,
  onCloseDrawer,
}: {
  target: NotificationLinkTarget
  children: ReactNode
  onCloseDrawer: () => void
}) {
  const navigate = useNavigate()
  const [path, hash = ''] = target.href.split('#')

  return (
    <button
      type="button"
      className="text-[rgb(21_151_255)] underline font-medium hover:text-[#0d7acc]"
      onClick={() => {
        onCloseDrawer()
        navigate(hash ? { pathname: path, hash: `#${hash}` } : path)
      }}
    >
      {children}
    </button>
  )
}

export function NotificationDetailPanel({
  mailContent,
  detail,
  onBack,
  onCloseDrawer,
}: NotificationDetailPanelProps) {
  const section = buildNotificationDetailSection(mailContent, detail)
  const isCrm = ['TRAINING', 'MEETING', 'TODO'].includes(detail.notes_for ?? '')
  const senderName = `${mailContent.first_name} ${mailContent.last_name}`.trim()

  return (
    <div
      data-testid="invitee-user-drawer-notification-detail"
      className="p-4"
    >
      <button
        type="button"
        data-testid="invitee-user-drawer-notification-detail-back"
        onClick={onBack}
        className="mb-4 flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900"
      >
        <ChevronRight className="h-4 w-4 rotate-180" />
        Back to list
      </button>

      <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-4">
        <div>
          <p className="text-sm font-bold text-[#344675]">
            From - {senderName}
          </p>
          <p className="text-sm font-bold text-[#344675]">{section.subtitle}</p>
          <p className="text-xs text-gray-400 mt-1">
            {mailContent.created_at
              ? format(
                  notificationCreatedAtDate(mailContent.created_at),
                  'yyyy-MM-dd hh:mm:ss a',
                )
              : ''}
          </p>
        </div>

        {isCrm && section.hereLink ? (
          <p className="text-sm text-gray-800">
            Click{' '}
            <NotificationLink target={section.hereLink} onCloseDrawer={onCloseDrawer}>
              here
            </NotificationLink>{' '}
            to view details.
          </p>
        ) : null}

        {section.showMention && section.hereLink ? (
          <p className="text-sm text-gray-800">
            <strong>{senderName}</strong> mentioned you in a note, click{' '}
            <NotificationLink target={section.hereLink} onCloseDrawer={onCloseDrawer}>
              here
            </NotificationLink>{' '}
            to view full context of the mention.
          </p>
        ) : null}

        {section.sectionLabel ? (
          <p className="text-sm font-medium text-gray-800">{section.sectionLabel}</p>
        ) : null}

        {section.refLink && detail.ref_name ? (
          <p className="text-sm">
            <NotificationLink target={section.refLink} onCloseDrawer={onCloseDrawer}>
              {detail.ref_name}
            </NotificationLink>
          </p>
        ) : null}

        {section.crmSchedule ? (
          <div className="space-y-1 text-sm text-gray-800">
            <p>
              <span className="font-bold">Starts On</span> {section.crmSchedule.startLabel}
            </p>
            <p>
              <span className="font-bold">Ends On</span> {section.crmSchedule.endLabel}
            </p>
          </div>
        ) : null}

        {section.bodyHtml ? (
          <div className="pt-2 border-t border-gray-100">
            <p className="text-sm text-gray-800">
              Note:{' '}
              <span
                className="prose prose-sm max-w-none inline"
                dangerouslySetInnerHTML={{ __html: section.bodyHtml }}
              />
            </p>
          </div>
        ) : null}
      </div>
    </div>
  )
}
